import React, { useState } from 'react';
import {
  Box, Typography, Table, TableBody, TableCell, TableHead, TableRow,
  IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import supabase from '../../../supabaseClient';

function PaymentHistory({ payments, onDeleted, onEdited }) {
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [editTarget, setEditTarget] = useState(null);
  const [editAmount, setEditAmount] = useState('');
  const [editNote, setEditNote] = useState('');
  const [editDate, setEditDate] = useState('');

  const openEdit = (p) => {
    setEditTarget(p);
    setEditAmount(String(p.amount));
    setEditNote(p.note || '');
    setEditDate('');
  };

  const confirmEdit = async () => {
    if (!editTarget) return;
    if (!editAmount || isNaN(editAmount) || Number(editAmount) <= 0) {
      alert('Podaj prawidłową kwotę!');
      return;
    }
    if (!editDate) {
      alert('Wybierz datę wypłaty!');
      return;
    }
    const { error } = await supabase.from('payments').update({
      amount: Number(editAmount),
      note: editNote,
      date: editDate,
    }).eq('id', editTarget.id);
    if (!error) onEdited();
    setEditTarget(null);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    const { error } = await supabase.from('payments').delete().eq('id', deleteTarget.id);
    if (!error) onDeleted();
    setDeleteTarget(null);
  };

  if (payments.length === 0) {
    return (
      <Typography variant="body2" sx={{ color: 'text.secondary', pb: 1 }}>
        Brak wypłat w tym okresie
      </Typography>
    );
  }

  return (
    <>
      <Table size="small" sx={{ width: 'auto', minWidth: 400 }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ color: 'text.secondary', fontSize: '0.75rem', py: 0.5, pl: 0 }}>Data</TableCell>
            <TableCell sx={{ color: 'text.secondary', fontSize: '0.75rem', py: 0.5 }}>Notatka</TableCell>
            <TableCell sx={{ color: 'text.secondary', fontSize: '0.75rem', py: 0.5 }} align="right">Kwota</TableCell>
            <TableCell sx={{ py: 0.5 }} />
          </TableRow>
        </TableHead>
        <TableBody>
          {payments.map(p => (
            <TableRow key={p.id}>
              <TableCell sx={{ py: 0.5, pl: 0, fontSize: '0.85rem', color: 'text.secondary', border: 0 }}>{p.date}</TableCell>
              <TableCell sx={{ py: 0.5, fontSize: '0.85rem', color: 'text.secondary', fontStyle: p.note ? 'normal' : 'italic', border: 0 }}>{p.note || '—'}</TableCell>
              <TableCell sx={{ py: 0.5, fontSize: '0.85rem', color: 'success.main', fontWeight: 500, border: 0 }} align="right">{p.amount.toFixed(2)} zł</TableCell>
              <TableCell sx={{ py: 0.5, border: 0 }}>
                <Box sx={{ display: 'flex' }}>
                  <IconButton size="small" onClick={() => openEdit(p)}
                    sx={{ color: 'text.disabled', '&:hover': { color: 'primary.main' } }}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => setDeleteTarget(p)}
                    sx={{ color: 'text.disabled', '&:hover': { color: 'error.main' } }}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {/* Edit dialog */}
      <Dialog open={!!editTarget} onClose={() => setEditTarget(null)} fullWidth maxWidth="xs">
        <DialogTitle>Edytuj wpis</DialogTitle>
        <DialogContent>
          <TextField label="Kwota (zł)" type="number" fullWidth value={editAmount}
            onChange={e => setEditAmount(e.target.value)} sx={{ mt: 2 }} />
          <TextField label="Notatka" fullWidth value={editNote}
            onChange={e => setEditNote(e.target.value)} sx={{ mt: 2 }} />
          <TextField
            label="Data"
            type="date"
            fullWidth
            value={editDate}
            onChange={e => setEditDate(e.target.value)}
            onClick={e => e.target.showPicker?.()}
            InputLabelProps={{ shrink: true }}
            sx={{
              mt: 2,
              '& input:not([value=""])::-webkit-datetime-edit': { opacity: 1 },
              '& input[value=""]::-webkit-datetime-edit': { opacity: 0 },
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditTarget(null)}>Anuluj</Button>
          <Button variant="contained" onClick={confirmEdit}>Zapisz</Button>
        </DialogActions>
      </Dialog>

      {/* Delete dialog */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs">
        <DialogTitle>Usuń wpis</DialogTitle>
        <DialogContent>
          <Typography>Czy chcesz usunąć wpis o płatności?</Typography>
          {deleteTarget && (
            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
              {deleteTarget.date} — {deleteTarget.amount.toFixed(2)} zł{deleteTarget.note ? ` (${deleteTarget.note})` : ''}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Anuluj</Button>
          <Button variant="contained" color="error" onClick={confirmDelete}>Usuń</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default PaymentHistory;
